import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  SyncOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getOAuthTokens,
  refreshOAuthToken,
  revokeOAuthToken,
  OAUTH_TOKEN_STATUSES,
  type OAuthTokenInfo,
  type OAuthTokenFilters,
} from '@/api/integrationApi';
import { useAuth } from '@/hooks/useAuth';
import IntegrationNav from './IntegrationNav';

export default function OAuthTokenListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const canRead = hasPermission('integration', 'read');
  const canWrite = hasPermission('integration', 'write');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: OAuthTokenFilters = { ...params };
      if (statusFilter) filters.status = statusFilter;
      return getOAuthTokens(filters);
    },
    [statusFilter]
  );

  const handleRefresh = useCallback(
    async (id: string) => {
      try {
        await refreshOAuthToken(id);
        message.success(t('integration.oauth_refresh_success'));
        gridRef.current?.reload();
      } catch {
        message.error(t('integration.oauth_refresh_failed'));
      }
    },
    [t]
  );

  const handleRevoke = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('integration.confirm_revoke_token'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await revokeOAuthToken(id);
            message.success(t('integration.oauth_revoke_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const statusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'expired': return 'orange';
      case 'revoked': return 'default';
      case 'error': return 'red';
      default: return 'default';
    }
  };

  const columns: ColumnsType<OAuthTokenInfo> = useMemo(
    () => [
      {
        title: t('integration.oauth_field.integration_config_id'),
        dataIndex: 'integration_config_id',
        key: 'integration_config_id',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('integration.oauth_field.user_id'),
        dataIndex: 'user_id',
        key: 'user_id',
        width: 150,
        ellipsis: true,
      },
      {
        title: t('integration.oauth_field.token_type'),
        dataIndex: 'token_type',
        key: 'token_type',
        width: 100,
      },
      {
        title: t('integration.oauth_field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (st: string) => (
          <Tag color={statusColor(st)}>{t(`integration.oauth_status.${st}`, st)}</Tag>
        ),
      },
      {
        title: t('integration.oauth_field.expires_at'),
        dataIndex: 'expires_at',
        key: 'expires_at',
        width: 170,
        render: (v: string) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('integration.oauth_field.granted_scopes'),
        dataIndex: 'granted_scopes',
        key: 'granted_scopes',
        width: 200,
        ellipsis: true,
        render: (v: string[]) => v?.join(', ') || '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 120,
        render: (_: unknown, record: OAuthTokenInfo) => (
          <Space size="small">
            {canWrite && record.status === 'active' && (
              <Button
                type="link"
                size="small"
                icon={<SyncOutlined />}
                onClick={() => handleRefresh(record.id)}
                title={t('integration.oauth_refresh')}
              />
            )}
            {canWrite && (record.status === 'active' || record.status === 'expired') && (
              <Button
                type="link"
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={() => handleRevoke(record.id)}
                title={t('integration.oauth_revoke')}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, canWrite, handleRefresh, handleRevoke]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('integration.oauth_field.status')}
        value={statusFilter}
        onChange={(v) => { setStatusFilter(v); gridRef.current?.reload(); }}
        style={{ width: 140 }}
        options={OAUTH_TOKEN_STATUSES.map((st) => ({
          value: st,
          label: t(`integration.oauth_status.${st}`),
        }))}
      />
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <IntegrationNav />
      <h2>{t('integration.oauth_tokens_title')}</h2>
      <DataGrid<OAuthTokenInfo>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
